import {Component, computed, input, output} from '@angular/core';
import {
  FloorPlanCanvasComponent,
  RenderedBackground,
  TableDecoration
} from '../../table-layout/manager/floor-plan-canvas.component';
import {FloorArea, FloorTable} from '../../table-layout/shared/models/table-layout.model';
import {FreeTable, OpenTable} from '../shared/open-tables.model';
import {FREE_TABLE_APPEARANCE, fmtEur, FRESH_APPEARANCE, IdleWarningLevelConfig} from '../shared/idle-warning-levels';

/**
 * The map half of the Offene-Tische overview: the **same** floor-plan canvas the Tischplan editor uses, put into
 * its read-only display mode (see {@link FloorPlanCanvasComponent.displayMode}) and fed markers instead of plain
 * table shapes.
 *
 * This component owns exactly three things the canvas deliberately does not: **what a marker says** (number, open
 * total, whether it carries a warning badge and in which colours), the **legend** that explains those colours, and
 * the **free-tables layer toggle**, which it projects into the canvas's own camera cluster (`canvasControl`) so the
 * map has one control stack rather than two. The camera — infinite pan, cursor-anchored wheel zoom, fit-all — is
 * entirely the canvas's, which is the point of reusing it: the live view and the editor cannot drift apart on how
 * the room is projected.
 *
 * Selection is by **booking-reference id**, not table id, because that is what the list selects on too. The
 * translation happens here (the canvas only knows tables), which keeps a single `selectedId` across both halves.
 */
@Component({
  selector: 'app-open-tables-map',
  standalone: true,
  imports: [FloorPlanCanvasComponent],
  styles: [`
    :host {
      display: block;
      position: relative;
      flex: 1;
      min-width: 0;
    }

    /* Bottom-left legend — the handover's frosted pill. Non-interactive so it never eats a pan that starts in
       the corner. */
    .otm-legend {
      position: absolute;
      left: 16px;
      bottom: 16px;
      display: flex;
      align-items: center;
      gap: 16px;
      flex-wrap: wrap;
      max-width: calc(100% - 110px);
      padding: 9px 15px;
      border-radius: 12px;
      background: rgba(255, 255, 255, .9);
      backdrop-filter: blur(8px);
      -webkit-backdrop-filter: blur(8px);
      box-shadow: 0 4px 14px -8px rgba(0, 0, 0, .22);
      font-size: 12.5px;
      font-weight: 600;
      color: #5c5f66;
      pointer-events: none;
      z-index: 6;
    }

    .otm-legend-entry {
      display: inline-flex;
      align-items: center;
      gap: 7px;
      white-space: nowrap;
    }

    .otm-legend-dot {
      width: 11px;
      height: 11px;
      border-radius: 999px;
      flex: 0 0 auto;
    }

    /* The app's global \`svg { fill: currentColor }\` beats the \`fill="none"\` presentation attribute the handover's
       outline icons rely on. The canvas restores it for its OWN icons, but projected content is scoped to this
       component, so the rule has to be repeated here. */
    svg[fill='none'] {
      fill: none;
    }

    /*
     * The free-tables layer toggle, projected into the canvas's camera cluster. Sized and shadowed exactly like
     * the canvas's own \`.fpc-zoom-card\` fit-all button so the four controls read as one stack, and given the
     * editor's accent treatment when the layer is on.
     */
    .otm-layer-btn {
      width: 46px;
      height: 46px;
      display: flex;
      align-items: center;
      justify-content: center;
      appearance: none;
      margin: 0;
      font: inherit;
      cursor: pointer;
      user-select: none;
      background: #fff;
      color: #3d3d40;
      border: 1px solid #eceae7;
      border-radius: 14px;
      box-shadow: 0 8px 24px -10px rgba(0, 0, 0, .25);
      transition: background .12s, color .12s, box-shadow .12s, transform .06s;
    }

    .otm-layer-btn:active {
      transform: translateY(1px);
    }

    .otm-layer-btn[aria-pressed='true'] {
      background: #c50e6c;
      color: #fff;
      border-color: #c50e6c;
      box-shadow: 0 10px 26px -8px rgba(197, 14, 108, .6);
    }
  `],
  template: `
    <app-floor-plan-canvas
      [displayMode]="true"
      [tables]="canvasTables()"
      [areas]="areas()"
      [background]="background()"
      [decorations]="decorations()"
      [selectedTableId]="selectedTableId()"
      [gridVisible]="false"
      (tableSelected)="onTableSelected($event)"
      (selectionCleared)="selectionCleared.emit()">
      <!-- A map LAYER control, so it belongs to the map's own control stack and not to the screen header — which
           is also what keeps the header at the reference's two rows on a phone. -->
      <button canvasControl type="button" class="otm-layer-btn" [attr.aria-pressed]="showFreeTables()"
              (click)="freeTablesToggled.emit()"
              aria-label="Freie Tische auf der Karte anzeigen" title="Freie Tische anzeigen">
        <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"
             stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
          <rect x="3" y="4" width="7" height="7" rx="1.6"/>
          <rect x="14" y="4" width="7" height="7" rx="3.5"/>
          <rect x="3" y="15" width="7" height="7" rx="3.5"/>
          <rect x="14" y="15" width="7" height="7" rx="1.6"/>
        </svg>
      </button>
    </app-floor-plan-canvas>

    <div class="otm-legend" role="note" aria-label="Legende der Wartezeiten">
      <span class="otm-legend-entry">
        <span class="otm-legend-dot" [style.background]="freshDot"></span>Aktiv
      </span>
      @for (level of levels(); track level.thresholdMinutes) {
        <span class="otm-legend-entry">
          <span class="otm-legend-dot" [style.background]="level.textColor"></span>{{ level.thresholdMinutes }}+ Min
        </span>
      }
      @if (showFreeTables()) {
        <span class="otm-legend-entry">
          <span class="otm-legend-dot" [style.background]="freeDot"></span>Frei
        </span>
      }
    </div>
  `
})
export class OpenTablesMapComponent {
  /** The open rows. Only those carrying a table are drawn — the rest are the list-only ad-hoc case. */
  readonly tables = input<readonly OpenTable[]>([]);
  /** Tables with nothing open on them, drawn muted when {@link showFreeTables} is on. */
  readonly freeTables = input<readonly FreeTable[]>([]);
  readonly areas = input<readonly FloorArea[]>([]);
  readonly background = input<RenderedBackground | null>(null);
  /** The configured escalation levels — the legend's rows. */
  readonly levels = input<readonly IdleWarningLevelConfig[]>([]);
  /**
   * Whether the free-table layer is drawn. Owned by the host, not by this component: the preference is persisted
   * per browser and has to survive the map being unmounted in the list-only view.
   */
  readonly showFreeTables = input(false);
  /** Selected **booking-reference** id (see the class docs). */
  readonly selectedId = input<string | null>(null);

  readonly selected = output<string>();
  readonly selectionCleared = output<void>();
  /** The layer toggle in the camera cluster was pressed — the host flips and persists {@link showFreeTables}. */
  readonly freeTablesToggled = output<void>();

  protected readonly freshDot = FRESH_APPEARANCE.legendDot;
  protected readonly freeDot = FREE_TABLE_APPEARANCE.legendDot;

  /** Open rows that actually have geometry, paired with their table. */
  private readonly placed = computed(() =>
    this.tables()
      .map(row => ({row, table: row.table}))
      .filter((entry): entry is {row: OpenTable; table: FloorTable} => entry.table !== null));

  /**
   * The tables handed to the canvas: every open one, plus the free ones when the toggle is on.
   *
   * A free table shares its id with nothing else, so the two sets can simply be concatenated — but an open table
   * always wins, because {@link OpenTablesStore.freeTables} already excludes anything occupied.
   */
  protected readonly canvasTables = computed<FloorTable[]>(() => {
    const open = this.placed().map(entry => entry.table);
    return this.showFreeTables() ? [...this.freeTables().map(free => free.table), ...open] : open;
  });

  /** Marker decoration per table id — the level colours resolved by the store, plus the open total. */
  protected readonly decorations = computed<Record<string, TableDecoration>>(() => {
    const map: Record<string, TableDecoration> = {};

    if (this.showFreeTables()) {
      for (const free of this.freeTables()) {
        // A free table carries no total and no badge: it is context, not something to act on.
        map[free.table.id] = {
          number: free.number,
          detail: null,
          surface: FREE_TABLE_APPEARANCE.markerSurface,
          border: FREE_TABLE_APPEARANCE.markerBorder,
          textColor: FREE_TABLE_APPEARANCE.numberColor,
          badgeColor: null,
          muted: true
        };
      }
    }

    for (const {row, table} of this.placed()) {
      map[table.id] = {
        number: row.number,
        detail: fmtEur(row.total),
        surface: row.level.markerSurface,
        border: row.level.markerBorder,
        textColor: row.level.numberColor,
        badgeColor: row.level.badgeColor,
        muted: false
      };
    }
    return map;
  });

  /** The canvas selects on table ids, the screen on reference ids — translate one way here… */
  protected readonly selectedTableId = computed<string | null>(() => {
    const id = this.selectedId();
    if (!id) return null;
    return this.placed().find(entry => entry.row.id === id)?.table.id ?? null;
  });

  /** …and the other way here. A press on a *free* marker matches no open row and is simply ignored. */
  protected onTableSelected(tableId: string): void {
    const match = this.placed().find(entry => entry.table.id === tableId);
    if (match) this.selected.emit(match.row.id);
  }
}
